import React, { useEffect, useId, useState } from "react";

import { Skeleton } from "antd";
import { useInView } from "react-intersection-observer";
import { Link } from "react-router";

import { RequestProposalCardProps } from "./requestProposalCard.model";
import { FontIcon } from "@companion-core/shared/app/Components/FontIcon";
import { sanitizeHtml } from "@companion-core/shared/app/Utils/htmlSanitizer";
import { useRequestProposalCard } from "./hooks/useRequestProposalCard";
import { RequestStatus } from "@companion-core/shared/app/Components/Requests/RequestStatus";

import "@companion-core/web/src/Assets/Styles/scss/myRequestsProposalCard.scss";
import { getSanitizedDescriptionAlt } from "@companion-core/shared/app/Utils/componentHelper";

export const RequestProposalCard = ({
  testID,
  id,
  icon,
  iconOverlay,
  media,
  mediaAlt,
  title,
  subtitle,
  status,
  budget,
  beginningAddress,
  endingAddress,
  ariaLabel,
  href,
  tracked = false,
  position,
  nbItems,
  url,
  variant,
  vendorId,
  className,
  displaySkeleton,
  onClick,
}: RequestProposalCardProps) => {
  const { onProposalCardVisible } = useRequestProposalCard({
    position,
    nbItems,
    id,
    format: !!media,
    url,
    variant,
    vendorId,
  });
  const [isAlreadyTracked, setIsAlreadyTracked] = useState<boolean>(false);
  const { ref, inView } = useInView({
    /* Optional options */
    threshold: 0.8,
    triggerOnce: true,
    skip: isAlreadyTracked || !tracked,
  });

  useEffect(() => {
    if (inView && onProposalCardVisible && tracked) {
      onProposalCardVisible();
      if (!isAlreadyTracked) setIsAlreadyTracked(true);
    }
  }, [inView, onProposalCardVisible, tracked]);

  const linkLabelId = useId();
  const classNames = `request-proposal-card${className ? " " + className : ""}`;
  const content = (
    <>
      <div className={iconOverlay ? "request-proposal-card__icon-overlay" : ""} aria-hidden={true}>
        {(media || iconOverlay) && (
          <img
            className="request-proposal-card__image"
            src={media ?? ""}
            alt={getSanitizedDescriptionAlt(mediaAlt) ?? ""}
            data-testid={`${testID}__media`}
          />
        )}
        {iconOverlay && (
          <span className="request-proposal-card__icon-overlay-icon">
            <FontIcon name={iconOverlay} size={30} aria-hidden={true} />
          </span>
        )}
      </div>
      {icon && (
        <FontIcon
          className="request-proposal-card__icon"
          data-testid={`${testID}__icon`}
          name={icon}
          size={24}
          aria-hidden={true}
        />
      )}
      {!media && !icon && displaySkeleton && (
        <div data-testid={`${testID}__skeleton-image`} aria-hidden={true}>
          <Skeleton.Image className="request-proposal-card__image" />
        </div>
      )}
      <div className="request-proposal-card__texts">
        {title && (
          <p className="body-text-5" data-testid={`${testID}__title`}>
            {title}
          </p>
        )}
        {subtitle && (
          <div
            className={icon ? "body-text-8" : "body-text-6"}
            data-testid={`${testID}__subtitle`}
            // nosemgrep: typescript.react.security.audit.react-dangerouslysetinnerhtml.react-dangerouslysetinnerhtml
            dangerouslySetInnerHTML={{ __html: sanitizeHtml(subtitle) }}
          />
        )}
        {beginningAddress && (
          <div
            className="body-text-6 request-proposal-card__addresses"
            data-testid={`${testID}__addresses`}>
            <span>{beginningAddress.formatted_address}</span>
            {endingAddress && (
              <>
                <FontIcon name="JP-Right" size={24} aria-hidden={true} />
                <span>{endingAddress.formatted_address}</span>
              </>
            )}
          </div>
        )}
        {status && (
          <div
            data-testid="request-proposal-card__status"
            className="request-proposal-card__status">
            <RequestStatus status={status} />
          </div>
        )}
        {budget && (
          <p
            className="body-text-5"
            data-testid={`${testID}__budget`}
            // nosemgrep: typescript.react.security.audit.react-dangerouslysetinnerhtml.react-dangerouslysetinnerhtml
            dangerouslySetInnerHTML={{ __html: sanitizeHtml(budget) }}
          />
        )}
      </div>
      <div
        className="request-proposal-card__arrow"
        data-testid={`${testID}__arrow`}
        aria-hidden={true}>
        <FontIcon size={16} name="JP-Arrow-right-bold" aria-hidden={true} />
      </div>
    </>
  );

  if (href) {
    return (
      <Link
        ref={ref}
        to={href}
        state={{ canGoBack: true }}
        className={classNames}
        onClick={onClick}
        tabIndex={0}
        aria-labelledby={ariaLabel ? linkLabelId : undefined}
        data-testid={testID}>
        {ariaLabel && (
          <span id={linkLabelId} className="request-proposal-card__sr-only">
            {ariaLabel}
          </span>
        )}
        {content}
      </Link>
    );
  }

  return (
    <div
      ref={ref}
      role="button"
      tabIndex={0}
      className={classNames}
      onClick={onClick}
      onKeyDown={(event) => {
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          onClick?.();
        }
      }}
      aria-label={ariaLabel}
      data-testid={testID}>
      {content}
    </div>
  );
};
